<template>
	<div id="wrapper">
		<SidebarView :cashier="true" :cashierVisible="true" :cdc="true" />
		<div class="d-flex flex-column" id="content-wrapper">
			<div id="content">
				<TopbarView />
				<div class="mx-3">
					<div class="card card-shape home-box mb-3">
						<div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
							<h6 class="m-0 fw-bold text-green">Daily Closure (CDC)</h6>
							<div class="d-flex gap-2">
								<b-button class="akkurate-green-btn btn-size text-white" :disabled="loading" @click="loadToday">
									<font-awesome-icon class="me-1" :icon="['fas', 'rotate']" />
									Refresh
								</b-button>
							</div>
						</div>
						<div class="card-body">
							<div class="row g-2 align-items-end mb-3">
								<div class="col-md-3">
									<label class="akkurate-dark small">Session date</label>
									<b-form-input v-model="filters.date" type="date" class="field-container" @change="loadToday" />
								</div>
								<div class="col-md-5">
									<label class="akkurate-dark small">Branch</label>
									<multiselect
										v-model="filters.branch"
										:options="branches"
										label="name"
										track-by="id"
										placeholder="Select branch..."
										class="field-container"
										@select="loadToday"
									/>
								</div>
								<div class="col-md-auto d-flex gap-2">
									<b-button
										class="akkurate-green-btn btn-size text-white"
										:disabled="!filters.branch?.id || opening"
										@click="openSession"
									>
										<b-spinner v-if="opening" small class="me-1" />
										Open Session
									</b-button>
								</div>
							</div>

							<div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

							<div v-if="!loading && !session" class="text-muted small py-3 text-center">
								No session found for this date/branch. Click <strong>Open Session</strong> to start.
							</div>

							<div v-if="session" class="border rounded p-3 mb-3">
								<div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
									<div class="small">
										<div><strong>Opening balance:</strong> {{ session.opening_balance ?? "—" }}</div>
										<div><strong>Expected closing:</strong> {{ session.expected_closing_balance ?? "—" }}</div>
										<div><strong>Calculated closing:</strong> {{ summary.calculated_closing_balance ?? "—" }}</div>
									</div>
									<div class="d-flex flex-wrap align-items-center gap-2">
										<span class="badge" :class="statusBadge(session.status)">{{ session.status || "—" }}</span>
										<b-button
											variant="outline-secondary"
											class="btn-size"
											:disabled="recomputing"
											@click="recompute"
										>
											<b-spinner v-if="recomputing" small class="me-1" />
											Recompute
										</b-button>
									</div>
								</div>
							</div>

							<div v-if="session" class="mb-3">
								<h6 class="fw-bold text-green mb-2">Daily Summary</h6>
								<div class="table-responsive">
									<table class="table table-bordered small mb-0">
										<tbody>
											<tr>
												<th>Deposits</th><td>{{ summary.total_deposits ?? "—" }}</td>
												<th>Withdrawals</th><td>{{ summary.total_withdrawals ?? "—" }}</td>
											</tr>
											<tr>
												<th>Loan Disbursed</th><td>{{ summary.total_loans_disbursed ?? "—" }}</td>
												<th>Repayments</th><td>{{ summary.total_repayments ?? "—" }}</td>
											</tr>
											<tr>
												<th>Fees</th><td>{{ summary.total_fees ?? "—" }}</td>
												<th>Cash In</th><td>{{ summary.total_cash_in ?? "—" }}</td>
											</tr>
											<tr>
												<th>Cash Out</th><td>{{ summary.total_cash_out ?? "—" }}</td>
												<th>Calculated Closing</th><td><strong>{{ summary.calculated_closing_balance ?? "—" }}</strong></td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>

							<div v-if="session" class="border rounded p-3">
								<h6 class="fw-bold text-green mb-2">Closure</h6>

								<div v-if="session.status !== 'open'" class="alert alert-warning py-2 small mb-2">
									Closure is locked while status is <strong>{{ session.status }}</strong>.
									<span v-if="session.status === 'pending_review'"> Awaiting supervisor approval.</span>
								</div>

								<div v-if="session.physical_closing_balance != null" class="small mb-2">
									<strong>Physical closing:</strong> {{ session.physical_closing_balance }}&nbsp;&nbsp;
									<strong>Variance:</strong> {{ session.variance ?? "—" }}
								</div>

								<div class="row g-2" :class="{ 'opacity-50': session.status !== 'open' }">
									<div class="col-12">
										<p class="small text-muted mb-2">
											Enter quantities for each denomination. Total will be computed automatically.
										</p>
									</div>
									<div class="col-12">
										<div class="table-responsive border rounded">
											<table class="table table-sm table-bordered mb-0 small">
												<thead class="table-light">
													<tr>
														<th style="width: 35%">Denomination</th>
														<th style="width: 25%">Quantity</th>
														<th style="width: 40%">Total</th>
													</tr>
												</thead>
												<tbody>
													<tr v-for="(row, idx) in denomRows" :key="row.denom + '-' + idx">
														<td>{{ row.denom }}</td>
														<td>
															<b-form-input
																v-model.number="row.qty"
																type="number"
																min="0"
																step="1"
																:disabled="session.status !== 'open' || submitting"
																class="field-container"
															/>
														</td>
														<td>{{ (row.denom * (row.qty || 0)).toFixed(2) }}</td>
													</tr>
												</tbody>
											</table>
										</div>
									</div>
									<div class="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
										<div class="small">
											<strong>Physical closing balance (computed):</strong> {{ computedPhysicalTotal.toFixed(2) }}
										</div>
										<b-button
											variant="danger"
											class="btn-size text-white"
											:disabled="session.status !== 'open' || submitting"
											@click="confirmSubmit"
										>
											<b-spinner v-if="submitting" small class="me-1" />
											Submit closure
										</b-button>
									</div>
								</div>
							</div>

							<div v-if="session && session.auditLogs && session.auditLogs.length" class="mt-3">
								<h6 class="fw-bold text-green mb-2">Audit logs</h6>
								<b-table
									striped
									bordered
									responsive
									:items="session.auditLogs"
									:fields="auditFields"
									show-empty
									class="table_text_size"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<footer></footer>
		</div>
		<ChatBot />
		<a class="d-inline scroll-to-top" href="#page-top"><i class="fas fa-angle-up"></i></a>
	</div>
</template>

<script>
import SidebarView from "@/views/main/components/Sidebar.vue";
import TopbarView from "@/views/main/components/Topbar.vue";
import ChatBot from "@/views/components/AI/ChatBot.vue";
import ApiService from "@/core/services/api.service";
import Multiselect from "vue-multiselect";

export default {
	name: "CashierDailyClosure",
	components: { SidebarView, TopbarView, ChatBot, Multiselect },
	data() {
		return {
			loading: false,
			opening: false,
			recomputing: false,
			submitting: false,
			error: "",
			branches: [],
			filters: {
				date: new Date().toISOString().slice(0, 10),
				branch: null,
			},
			session: null,
			denomRows: [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1].map((d) => ({
				denom: d,
				qty: 0,
			})),
			auditFields: [
				{ key: "action", label: "Action" },
				{ key: "performed_by", label: "By" },
				{ key: "created_at", label: "At" },
				{ key: "metadata", label: "Meta" },
			],
		};
	},
	computed: {
		summary() {
			return this.session?.cashier_daily_summary || {};
		},
		computedPhysicalTotal() {
			return this.denomRows.reduce((sum, r) => {
				const qty = Number(r.qty || 0);
				return sum + Number(r.denom) * (Number.isFinite(qty) ? qty : 0);
			}, 0);
		},
	},
	created() {
		this.loadBranches();
	},
	methods: {
		statusBadge(status) {
			const s = String(status || "").toLowerCase();
			if (s === "open") return "bg-info text-dark";
			if (s === "pending_review") return "bg-warning text-dark";
			if (s === "closed") return "bg-success";
			if (s === "rejected") return "bg-danger";
			return "bg-secondary";
		},
		async loadBranches() {
			try {
				const res = await ApiService.get("/settings/branches/dropdown");
				this.branches = res.data?.data || res.data?.branches || res.data || [];
			} catch (e) {
				this.branches = [];
			}
		},
		resetDenomsFromSession() {
			const denoms = this.session?.denominations;
			const map = new Map();
			if (Array.isArray(denoms)) {
				for (const d of denoms) {
					if (d && d.denomination != null) {
						map.set(Number(d.denomination), Number(d.quantity || 0));
					}
				}
			}
			this.denomRows = [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1].map((v) => ({
				denom: v,
				qty: map.has(v) ? map.get(v) : 0,
			}));
		},
		async loadToday() {
			if (!this.filters.branch?.id) return;
			this.loading = true;
			this.error = "";
			this.$Progress.start();
			try {
				const res = await ApiService.get("/cashier-sessions/today", {
					params: { date: this.filters.date, branch_id: this.filters.branch.id },
				});
				this.session = res.data?.data || null;
				if (this.session) {
					this.resetDenomsFromSession();
				}
			} catch (e) {
				this.session = null;
				this.error = e.response?.data?.message || "Could not load cashier session.";
			} finally {
				this.loading = false;
				this.$Progress.finish();
			}
		},
		async openSession() {
			if (!this.filters.branch?.id) return;
			this.opening = true;
			this.error = "";
			this.$Progress.start();
			try {
				const res = await ApiService.post("/cashier-sessions/open", {
					branch_id: this.filters.branch.id,
					session_date: this.filters.date || undefined,
				});
				this.session = res.data?.data || null;
				if (this.session) {
					this.resetDenomsFromSession();
				}
				toast.fire({ icon: "success", title: "Session opened" });
			} catch (e) {
				this.error = e.response?.data?.message || "Could not open session.";
			} finally {
				this.opening = false;
				this.$Progress.finish();
			}
		},
		async recompute() {
			if (!this.session?.id) return;
			this.recomputing = true;
			this.error = "";
			this.$Progress.start();
			try {
				const res = await ApiService.post(`/cashier-sessions/${this.session.id}/recompute`);
				const summary = res.data?.data || res.data || null;
				if (summary) {
					this.session = { ...this.session, cashier_daily_summary: summary };
				}
				toast.fire({ icon: "success", title: "Recomputed" });
			} catch (e) {
				this.error = e.response?.data?.message || "Recompute failed.";
			} finally {
				this.recomputing = false;
				this.$Progress.finish();
			}
		},
		denomsPayload() {
			return this.denomRows.map((r) => ({
				denomination: Number(r.denom),
				quantity: Math.max(0, parseInt(r.qty || 0, 10) || 0),
			}));
		},
		confirmSubmit() {
			if (this.session?.status !== "open") return;
			swal
				.fire({
					title: "Submit closure?",
					text: "This will submit physical cash closing for review/closure. Continue?",
					icon: "warning",
					showCancelButton: true,
					confirmButtonText: "Submit",
					cancelButtonText: "Cancel",
					buttonsStyling: false,
					customClass: {
						confirmButton: "btn btn-size akkurate-green-btn text-white me-2",
						cancelButton: "btn btn-size akkurate-gray-btn text-white",
					},
				})
				.then(async (r) => {
					if (!r.isConfirmed) return;
					await this.submitClosure();
				});
		},
		async submitClosure() {
			if (!this.session?.id) return;
			this.submitting = true;
			this.error = "";
			this.$Progress.start();
			try {
				const res = await ApiService.post(
					`/cashier-sessions/${this.session.id}/submit-closure`,
					{ denominations: this.denomsPayload() },
				);
				this.session = res.data?.data || null;
				toast.fire({ icon: "success", title: res.data?.message || "Closure submitted" });
				if (this.session) {
					this.resetDenomsFromSession();
				}
			} catch (e) {
				this.error = e.response?.data?.message || "Submit closure failed.";
			} finally {
				this.submitting = false;
				this.$Progress.finish();
			}
		},
	},
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

